import React, { useEffect, useContext } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import styles from "../../DeliveryTools.module.scss";
import { Box, Typography, Link } from "@mui/material";
import EMPButton from "../../../../shared/components/EMPButton/EMPButton";
import CheckIcon from "@mui/icons-material/Check";
import ddtqr from "./img/ddt_qr.jpg";
import iPad22x from "./img/ipad_final2@2x.jpg";
import iPad2x from "./img/ipad_final@2x.jpg";
import { Fade } from "react-slideshow-image";
import "react-slideshow-image/dist/styles.css";
import { withTranslation } from "react-i18next";
import * as AnalyticsService from "../../../../shared/services/analytics/AnalyticsService";
import { ModalContext } from "../../../../shared/components/EMPModal/ModalContext";
import DownloadInstructions from "../../../../shared/components/EMPModal/ModalFragments/DownloadInstructions";
import { Link as RouterLink } from "react-router-dom";
import { getMarketBrandPath } from "../../../../utils/marketBrandUtils";

const analyticsPageName = "fv:emp:delivery tools";

const Hero = (props) => {
  const { t } = props;

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(12, 1fr)",
        columnGap: 4,
        py: 8,
        backgroundColor: "#f2f2f2",
        borderRadius: "32px",
      }}
    >
      <Box
        sx={{
          gridColumn: { xs: "2 / 12", sm: "2 / 12", md: "3 / 11" },
          mt: 4,
          zIndex: 0,
          textAlign: "center",
        }}
      >
        <Typography variant="h1" sx={{ mb: 10 }}>
          {t("DeliveryTools.hero.title")}
        </Typography>

        <Typography variant="body1" sx={{ mb: 7 }}>
          {t("DeliveryTools.hero.subTitle")}
        </Typography>

        <Typography variant="body1" sx={{ mb: 4 }}>
          {t("DeliveryTools.hero.scan")}
        </Typography>

        <Box
          sx={{
            mb: 4,
          }}
        >
          <Box>
            <img
              src={ddtqr}
              width="320"
              alt={t(
                "DeliveryTools.hero.qrAlt",
                "QR code to download Digital Delivery Tool"
              )}
            />
          </Box>
        </Box>

        <Typography variant="body1" component="div">
          {t("DeliveryTools.hero.download.0")}
          <Typography
            variant="subtitle1"
            component="div"
            sx={{ display: "inline" }}
          >
            {" "}
            {t("DeliveryTools.hero.download.1")}{" "}
          </Typography>
          {t("DeliveryTools.hero.download.2")}
        </Typography>
      </Box>
    </Box>
  );
};

Hero.propTypes = {
  t: PropTypes.func.isRequired,
};

const Checklist = (props) => {
  const { text, user } = props;
  const dealerReportingPath =
    user.userType === "na_dealer"
      ? getMarketBrandPath(user.market, user.brand, "/dealer-reporting")
      : "/dealer-reporting";

  return (
    <Grid container spacing={10} justifyContent="center" sx={{ mb: 20 }}>
      {text.map((item) => {
        return (
          <Grid
            item
            key={item.title}
            wrap="nowrap"
            xs={12}
            sm={6}
            md={6}
            lg={6}
            container
          >
            <Box mr={6} style={{ color: "#2575fc" }}>
              <CheckIcon color="inherit" />
            </Box>
            <Box>
              <Typography variant="h3" sx={{ mb: 4 }}>
                {item.title}
              </Typography>
              <Typography
                variant="body1"
                sx={
                  item.linkToReporting
                    ? { display: "inline" }
                    : { display: "block" }
                }
              >
                {item.subtitle}
              </Typography>
              {item.linkToReporting && (
                <Link
                  component={RouterLink}
                  to={dealerReportingPath}
                  variant="body1"
                  underline="always"
                  color="text.primary"
                  sx={{ ml: 1 }}
                >
                  Find it here
                </Link>
              )}
            </Box>
          </Grid>
        );
      })}
    </Grid>
  );
};

Checklist.propTypes = {
  text: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      subtitle: PropTypes.string.isRequired,
      linkToReporting: PropTypes.bool,
    })
  ).isRequired,
  user: PropTypes.shape({
    userType: PropTypes.string,
    brand: PropTypes.string,
    market: PropTypes.string,
  }).isRequired,
};

const Slideshow = (props) => {
  const { slides } = props;
  return (
    <Grid container alignItems="center">
      <Grid item sx={{ width: 480 }}>
        <Box>
          <Fade transitionDuration={500} duration={4000} arrows={false}>
            {slides.map((slide) => (
              <div key={slide.image}>
                <img
                  style={{ width: "100%" }}
                  src={slide.image}
                  alt={slide.alt}
                />
              </div>
            ))}
          </Fade>
        </Box>
      </Grid>
    </Grid>
  );
};

Slideshow.propTypes = {
  slides: PropTypes.arrayOf(
    PropTypes.shape({
      image: PropTypes.string.isRequired,
      alt: PropTypes.string.isRequired,
    })
  ).isRequired,
};

const DownloadTool = ({ t, user }) => {
  const { handleModal } = useContext(ModalContext);

  useEffect(() => {
    // initial page load
    AnalyticsService.trackPageLoadEvent(analyticsPageName, user);
  }, [user]);

  const modalOpenAnalytics = () => {
    AnalyticsService.trackOnClickEvent(
      analyticsPageName,
      "emp:delivery tools:content:view download instructions",
      "fv:emp:delivery tools:content action",
      user
    );
  };

  const checklist = [
    {
      title: t("DeliveryTools.checklist.1.title"),
      subtitle: t("DeliveryTools.checklist.1.subtitle"),
    },
    {
      title: t("DeliveryTools.checklist.2.title"),
      subtitle: t("DeliveryTools.checklist.2.subtitle"),
      linkToReporting: true,
    },
    {
      title: t("DeliveryTools.checklist.3.title"),
      subtitle: t("DeliveryTools.checklist.3.subtitle"),
    },
    {
      title: t("DeliveryTools.checklist.4.title"),
      subtitle: t("DeliveryTools.checklist.4.subtitle"),
    },
  ];

  const npsData = [
    {
      title: t("DeliveryTools.nps.1.number"),
      subtitle: t("DeliveryTools.nps.1.subtitle"),
    },
    {
      title: t("DeliveryTools.nps.2.number"),
      subtitle: t("DeliveryTools.nps.2.subtitle"),
    },
    {
      title: t("DeliveryTools.nps.3.number"),
      subtitle: t("DeliveryTools.nps.3.subtitle"),
    },
    {
      title: t("DeliveryTools.nps.4.number"),
      subtitle: t("DeliveryTools.nps.4.subtitle"),
    },
  ];

  const screenshots = [
    {
      image: iPad2x,
      alt: t(
        "DeliveryTools.slideshow.alt.1",
        "Digital Delivery Tool app screenshot"
      ),
    },
    {
      image: iPad22x,
      alt: t(
        "DeliveryTools.slideshow.alt.2",
        "Digital Delivery Tool app screenshot"
      ),
    },
  ];

  return (
    <>
      <Hero t={t}></Hero>
      <section className={styles.pageContainer}>
        <Grid container>
          <Grid item xs={12} container>
            <Typography variant="h2" sx={{ mt: 8, mb: 10 }}>
              {t("DeliveryTools.checklist.title")}
            </Typography>
            <Checklist text={checklist} user={user} />
          </Grid>
        </Grid>

        <Grid container>
          <Grid item xs={12} container>
            <Typography variant="h2" sx={{ mt: 6, mb: 6 }}>
              {t("DeliveryTools.nps.title")}
            </Typography>
          </Grid>
          <Grid item xs={12} container>
            {npsData.map((item) => (
              <Grid item xs={3} key={item.title}>
                <Grid
                  item
                  wrap="nowrap"
                  container
                  alignItems="center"
                  sx={{ mb: 6 }}
                >
                  <Box mr={4}>
                    <Typography variant="h1">{item.title}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body1">{item.subtitle}</Typography>
                  </Box>
                </Grid>
              </Grid>
            ))}
          </Grid>
        </Grid>

        <Grid container>
          <Grid item xs={12}>
            <Typography variant="caption">
              {t("DeliveryTools.nps.caption")}
            </Typography>
          </Grid>
        </Grid>

        <Grid container sx={{ my: 13 }}>
          <Grid item xs={12}>
            <Slideshow slides={screenshots} />
          </Grid>
        </Grid>

        <Grid container>
          <Grid item xs={12}>
            <Typography variant="h2" sx={{ mb: 5 }}>
              {t("DeliveryTools.footer.title")}
            </Typography>
            <Typography variant="body1" sx={{ mb: 4 }}>
              {t("DeliveryTools.footer.subtitle")}
            </Typography>
            <Box sx={{ mb: 20 }}>
              <EMPButton
                variant="contained"
                onClick={() => {
                  modalOpenAnalytics();
                  handleModal(
                    t("DeliveryTools.downloadInstructions.title"),
                    <DownloadInstructions />
                  );
                }}
              >
                {t("common.buttonCtas.learnHow")}
              </EMPButton>
            </Box>
          </Grid>
        </Grid>
      </section>
    </>
  );
};

DownloadTool.propTypes = {
  t: PropTypes.func.isRequired,
  user: PropTypes.shape({}).isRequired,
};

export default withTranslation("emp")(DownloadTool);
